import React, { useEffect, useState } from 'react';
import { Modal, Button } from 'antd';
import moment from 'moment';

const DeleteEmployeeModal = ({
  isOpen,
  profile,
  onClose,
  onConfirm,
  confirmLoading = false,
}) => {
  const [useLastWorkingDay, setUseLastWorkingDay] = useState(false);
  const [lastWorkingDate, setLastWorkingDate] = useState('');
  const [dateError, setDateError] = useState('');

  const employeeName = `${profile?.firstname || ''} ${profile?.lastname || ''}`.trim() || 'this employee';
  const today = moment().format('YYYY-MM-DD');
  const joinDate = profile?.workAnniversaryDate
    ? moment(profile.workAnniversaryDate).format('YYYY-MM-DD')
    : '';

  useEffect(() => {
    if (!isOpen) {
      setUseLastWorkingDay(false);
      setLastWorkingDate('');
      setDateError('');
    }
  }, [isOpen, profile?.userId]);

  const handleConfirm = () => {
    if (useLastWorkingDay) {
      if (!lastWorkingDate) {
        setDateError('Please select a last working day.');
        return;
      }
      if (joinDate && lastWorkingDate < joinDate) {
        setDateError('Last working day cannot be before the join date.');
        return;
      }
      if (lastWorkingDate > today) {
        setDateError('Last working day cannot be in the future.');
        return;
      }
    }

    onConfirm({
      userId: profile?.userId,
      lastWorkingDate: useLastWorkingDay ? lastWorkingDate : null,
    });
  };

  return (
    <Modal
      title="Delete employee"
      open={isOpen}
      zIndex={1200}
      onCancel={confirmLoading ? undefined : onClose}
      closable={!confirmLoading}
      maskClosable={!confirmLoading}
      keyboard={!confirmLoading}
      footer={[
        <Button key="cancel" onClick={onClose} disabled={confirmLoading}>
          Cancel
        </Button>,
        <Button
          key="delete-only"
          onClick={() => onConfirm({ userId: profile?.userId, lastWorkingDate: null })}
          disabled={confirmLoading}
        >
          Delete only
        </Button>,
        <Button
          key="confirm"
          type="primary"
          danger
          onClick={handleConfirm}
          loading={confirmLoading}
          disabled={confirmLoading}
        >
          {useLastWorkingDay ? 'Delete & apply salary' : 'Delete'}
        </Button>,
      ]}
    >
      <p className="text-sm text-[#27314a] mb-4">
        You are about to delete <strong>{employeeName}</strong>. Choose whether to record a last
        working day for salary proration in that month.
      </p>

      <label className="flex items-start gap-2 text-sm text-[#27314a] cursor-pointer mb-3">
        <input
          type="checkbox"
          checked={useLastWorkingDay}
          onChange={(e) => {
            setUseLastWorkingDay(e.target.checked);
            setDateError('');
            if (!e.target.checked) {
              setLastWorkingDate('');
            }
          }}
          disabled={confirmLoading}
          className="w-6 h-4 p-0"
        />
        <span>
          Set last working day (salary paid only for weekdays worked that month, minus public
          holidays)
        </span>
      </label>

      {useLastWorkingDay ? (
        <div className="mb-2">
          <label className="block text-xs font-medium text-[#6f7d97] mb-1" htmlFor="last-working-date">
            Last working day
          </label>
          <input
            id="last-working-date"
            type="date"
            className="w-full border border-[#e6ebf6] rounded-lg px-3 py-2 text-sm"
            max={today}
            min={joinDate || undefined}
            value={lastWorkingDate}
            onChange={(e) => {
              setLastWorkingDate(e.target.value);
              setDateError('');
            }}
            disabled={confirmLoading}
          />
          {joinDate ? (
            <p className="text-xs text-[#6f7d97] mt-1">Join date: {moment(joinDate).format('DD MMM, YYYY')}</p>
          ) : null}
          {dateError ? <p className="text-xs text-red-600 mt-1">{dateError}</p> : null}
          <p className="text-xs text-[#6f7d97] mt-2">
            Example: last day 15 Jun 2026 → pay for Mon–Fri from month start (or join date) through
            15 Jun, excluding weekends and public holidays. Remaining weekdays in the month are
            deducted from salary.
          </p>
        </div>
      ) : (
        <p className="text-xs text-[#6f7d97]">
          <strong>Delete only</strong> removes the employee without changing the current salary
          sheet. Use the checkbox above if they left mid-month and payroll should be prorated.
        </p>
      )}
    </Modal>
  );
};

export default DeleteEmployeeModal;
